<template>
  <Combobox :model-value="null" nullable @update:model-value="pick">
    <div class="po-search relative">
      <ComboboxInput
        ref="inputRef"
        class="input w-52 py-1.5"
        type="search"
        autocomplete="off"
        placeholder="Search PO…"
        aria-label="Search PO across all vendors and weeks"
        :display-value="() => query"
        @change="query = $event.target.value"
      />
      <ComboboxOptions v-if="query.trim()" class="absolute right-0 top-full z-50 mt-1 w-full rounded-md border border-gray-200 bg-white py-0.5 text-xs shadow-lg focus:outline-none">
        <li v-if="hits.length === 0" class="px-2.5 py-1 text-gray-500">No PO matches</li>
        <ComboboxOption v-for="hit in hits" :key="hitKey(hit)" v-slot="{ active }" :value="hit" as="template">
          <li class="flex cursor-pointer items-center gap-1.5 px-2.5 py-1" :class="active ? 'bg-blue-50 text-blue-900' : 'text-gray-700'">
            <span class="w-20 shrink-0 truncate font-medium text-gray-900">{{ groupLabel(hit.group) }}</span>
            <span class="w-8 shrink-0 font-semibold text-gray-500">{{ hit.lane.vendorCode }}</span>
            <span class="min-w-0 flex-1 truncate text-[11px] text-gray-500">{{ weekDatesLabel(hit.weekKey) }}</span>
          </li>
        </ComboboxOption>
        <li v-if="truncated" class="px-2.5 py-1 text-[11px] text-gray-400">Keep typing to narrow the list</li>
      </ComboboxOptions>
    </div>
  </Combobox>
</template>

<script setup lang="ts">
  import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/vue';
  import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

  import { weekDatesLabel } from '@/utils/poActivity';
  import { groupLabel, searchBlocks, singlePoHit } from '@/utils/scheduleBoard';
  import type { BlockSelection, VendorLane } from '@/utils/scheduleBoard';

  // A short prefix matches most of the board; the list stays a glance long and says so.
  const MAX_HITS = 8;
  // Long enough that a number typed in one go reveals once, not on every keystroke.
  const REVEAL_DEBOUNCE_MS = 250;

  const props = defineProps<{ lanes: VendorLane[] }>();
  // pick: reveal this block on the board. clear: the box was emptied, so drop the highlight it put there.
  const emit = defineEmits<{ pick: [found: BlockSelection]; clear: [] }>();

  const query = ref('');
  const inputRef = ref<InstanceType<typeof ComboboxInput> | null>(null);
  const allHits = computed(() => searchBlocks(props.lanes, query.value));
  const hits = computed(() => allHits.value.slice(0, MAX_HITS));
  const truncated = computed(() => allHits.value.length > MAX_HITS);

  let revealTimer: ReturnType<typeof setTimeout> | null = null;
  // The label a pick wrote into the box, so the query watcher does not reveal it a second time.
  let pickedLabel: string | null = null;

  function cancelReveal(): void {
    if (revealTimer !== null) {
      clearTimeout(revealTimer);
      revealTimer = null;
    }
  }

  // Typing reveals on its own once the text names exactly one PO; until then the list is there
  // to choose from. The box keeps its text either way — it is the label of what is highlighted.
  watch(query, (value) => {
    cancelReveal();
    if (value === pickedLabel) return;
    pickedLabel = null;
    if (!value.trim()) {
      emit('clear');
      return;
    }
    revealTimer = setTimeout(() => {
      revealTimer = null;
      const hit = singlePoHit(allHits.value);
      if (hit) emit('pick', hit);
    }, REVEAL_DEBOUNCE_MS);
  });

  function hitKey(hit: BlockSelection): string {
    return `${hit.lane.vendorCode}|${hit.weekKey}|${hit.group.key}`;
  }

  // A pick completes the text to the PO it chose: the box then reads as the label of what is
  // highlighted, rather than the fragment that found it. Written to the element as well:
  // Headless UI owns the input's text and, mid-typing, ignores a display value that changes
  // under it, so the query alone would leave the fragment on screen.
  async function pick(found: BlockSelection | null): Promise<void> {
    if (!found) return;
    cancelReveal();
    pickedLabel = groupLabel(found.group);
    query.value = pickedLabel;
    emit('pick', found);
    await nextTick();
    const element = inputRef.value?.$el as HTMLInputElement | undefined;
    if (element) element.value = pickedLabel;
  }

  onBeforeUnmount(cancelReveal);
</script>
